import { StellarWalletsKit, Networks as KitNetworks } from "@creit.tech/stellar-wallets-kit";
import { defaultModules } from "@creit.tech/stellar-wallets-kit/modules/utils";
import { AlbedoModule } from "@creit.tech/stellar-wallets-kit/modules/albedo";
import { xBullModule } from "@creit.tech/stellar-wallets-kit/modules/xbull";
import {
  WALLET_CONNECT_ID,
  WalletConnectModule,
  WalletConnectTargetChain,
} from "@creit.tech/stellar-wallets-kit/modules/wallet-connect";
import { Networks } from '@stellar/stellar-sdk';
import {
  noWalletConnect,
  observeWalletConnectReadiness,
  WalletConnectReadiness,
} from './walletConnectReadiness';
import { describeCapabilityRefusal } from './walletConnectCapability';

const WALLET_STORAGE_KEY = 'geotrust_wallet_connected';
const WALLET_ADDRESS_KEY = 'geotrust_wallet_address';
const WALLET_TYPE_KEY = 'geotrust_wallet_type'; // Store which wallet was used

const WC_PROJECT_ID = process.env.REACT_APP_WALLETCONNECT_PROJECT_ID || '';

/** The kit's own type for the AppKit options it forwards. */
type WalletConnectModuleAppKitOptions =
  NonNullable<ConstructorParameters<typeof WalletConnectModule>[0]['appKitOptions']>;

/** Returns true when running on a mobile/tablet browser. */
export function isMobileBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/** Returns true when WalletConnect is configured (has a project ID). */
export function hasWalletConnect(): boolean {
  return !!WC_PROJECT_ID;
}

// Kit v2 is a static singleton, so initialization happens once per page rather
// than once per Wallet instance.
let kitInitialized = false;
let walletConnectReadiness: WalletConnectReadiness = noWalletConnect();

function buildWalletConnectModule(): WalletConnectModule | null {
  if (!WC_PROJECT_ID) return null;
  try {
    return new WalletConnectModule({
      projectId: WC_PROJECT_ID,
      metadata: {
        name: 'GeoTrust',
        description: 'Geo-driven Smart Contract Platform on Stellar',
        url: typeof window !== 'undefined' ? window.location.origin : 'https://geotrust.stellargeolink.com',
        icons: ['https://geotrust.stellargeolink.com/favicon.svg'],
      },
      // Kit v2 derives the WalletConnect namespace from allowedChains and
      // defaults to mainnet. GeoTrust is testnet-only, so state it explicitly.
      allowedChains: [WalletConnectTargetChain.TESTNET],
      // Reown AppKit has no Stellar network, so the kit hands it `[mainnet]`
      // purely to satisfy a required option. One consequence leaks into the
      // UI: the picker's "all wallets" list is fetched with `chains=eip155:1`,
      // so it browses Ethereum wallets — every one of which would refuse our
      // required stellar:testnet namespace if a user picked it. Hide that list
      // and keep the kit's featured Stellar wallets (Freighter, LOBSTR,
      // Scopuly), which are the ones that can actually serve the session.
      //
      // Typed as a full CreateAppKit by the kit, but it spreads this over its
      // own defaults, so a subset is what the runtime wants; passing
      // `networks` here would mean restating the Ethereum default we are
      // working around.
      appKitOptions: { allWallets: 'HIDE' } as WalletConnectModuleAppKitOptions,
    });
  } catch (e) {
    return null;
  }
}

function ensureKit(): void {
  if (kitInitialized) return;

  const mobile = isMobileBrowser();
  const wc = buildWalletConnectModule();
  let modules;

  if (mobile) {
    // On mobile, browser-extension wallets (xBull bridge, Rabet, Hana, Lobstr extension,
    // Freighter extension) all fail — they use popup/postMessage flows that mobile browsers
    // block. WalletConnect is the standard mobile protocol; Albedo is web-based and works
    // reasonably well in a mobile browser tab.
    if (wc) {
      // WalletConnect is configured: show it first, then Albedo as a fallback
      modules = [wc, new AlbedoModule()];
    } else {
      // No WalletConnect project ID yet — show Albedo + xBull (xBull last, may still fail)
      modules = [new AlbedoModule(), new xBullModule()];
    }
  } else {
    // Desktop: show all wallets; also append WalletConnect for QR-code scanning
    modules = defaultModules();
    if (wc) modules.push(wc);
  }

  if (wc) {
    walletConnectReadiness = observeWalletConnectReadiness(wc);
  }

  StellarWalletsKit.init({
    modules,
    network: KitNetworks.TESTNET,
    selectedWalletId: savedWalletType() ?? undefined,
  });

  kitInitialized = true;
}

function savedWalletType(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(WALLET_TYPE_KEY);
}

/** The wallet the kit currently has selected, if any. */
function activeWalletId(): string | undefined {
  try {
    return StellarWalletsKit.selectedModule?.productId;
  } catch {
    // The getter throws when no wallet has been selected yet.
    return undefined;
  }
}

/** Display name of the selected wallet, for user-facing messages. */
function activeWalletName(): string | undefined {
  try {
    return StellarWalletsKit.selectedModule?.productName;
  } catch {
    return undefined;
  }
}

export class Wallet {
  private connectedAddress: string | null = null;
  private isConnecting: boolean = false;

  constructor() {
    ensureKit();

    // Try to restore previous connection
    if (typeof window !== 'undefined') {
      const savedAddress = localStorage.getItem(WALLET_ADDRESS_KEY);
      if (savedAddress && Wallet.wasConnected()) {
        this.connectedAddress = savedAddress;
      }
    }
  }

  // Check if wallet was previously connected
  static wasConnected(): boolean {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(WALLET_STORAGE_KEY) === 'true';
  }

  // Save connection state
  private saveConnectionState(connected: boolean, address?: string, walletType?: string): void {
    if (typeof window === 'undefined') return;
    if (connected && address) {
      localStorage.setItem(WALLET_STORAGE_KEY, 'true');
      localStorage.setItem(WALLET_ADDRESS_KEY, address);
      if (walletType) {
        localStorage.setItem(WALLET_TYPE_KEY, walletType);
      }
      this.connectedAddress = address;
    } else {
      localStorage.removeItem(WALLET_STORAGE_KEY);
      localStorage.removeItem(WALLET_ADDRESS_KEY);
      localStorage.removeItem(WALLET_TYPE_KEY);
      this.connectedAddress = null;
    }
  }

  async connect(): Promise<void> {
    // Prevent multiple simultaneous connection attempts
    if (this.isConnecting) {
      throw new Error('Wallet connection already in progress');
    }

    this.isConnecting = true;

    try {
      // WalletConnect starts its relay handshake in its constructor and reports
      // itself unavailable until that lands. Wait for the handshake to settle so
      // the first tap works instead of failing with "WalletConnect is not
      // running yet". A failed handshake settles too, so this cannot hang; the
      // modal then simply shows WalletConnect as unavailable.
      let walletConnectError: string | null = null;
      try {
        await walletConnectReadiness.ready;
      } catch (error: any) {
        walletConnectError = error?.message || String(error);
      }

      const supportedWallets = await StellarWalletsKit.refreshSupportedWallets();
      const availableWallets = supportedWallets.filter(w => w.isAvailable);
      if (availableWallets.length === 0) {
        throw new Error(
          walletConnectError
            ? `No wallets available. ${walletConnectError}`
            : 'No wallets available. Please install a Stellar wallet like xBull, Albedo, or Lobstr.',
        );
      }

      // authModal picks the wallet, makes it the active module and requests the
      // address, replacing kit v1's openModal + setWallet + getAddress dance.
      const { address } = await StellarWalletsKit.authModal();
      if (!address) {
        throw new Error('No address returned from wallet');
      }

      this.saveConnectionState(true, address, activeWalletId());
    } catch (error: any) {
      this.saveConnectionState(false);
      const errorMsg = error?.message || error?.toString() || 'Unknown error';

      // A wallet whose WalletConnect bridge cannot serve testnet must refuse the
      // whole proposal, because we put stellar:testnet in requiredNamespaces.
      // That refusal frequently carries the word "rejected", so it has to be
      // recognised before the cancellation check below, which would otherwise
      // blame the user for something the wallet decided.
      if (activeWalletId() === WALLET_CONNECT_ID) {
        // The wallet's own words, kept intact: whether a refusal names the
        // chain or the method is the only way to tell a bridge that lacks
        // testnet from one that lacks stellar_signXDR, and the user-facing
        // message deliberately does not guess between them.
        console.warn('[walletconnect] session refused', {
          code: error?.code,
          message: errorMsg,
        });
        const refusal = describeCapabilityRefusal(error, activeWalletName());
        if (refusal) throw new Error(refusal.message);
      }

      // Check if user rejected/cancelled
      if (errorMsg.includes('rejected') || errorMsg.includes('cancelled') || errorMsg.includes('denied') || errorMsg.includes('closed')) {
        throw new Error('Wallet connection was cancelled by user');
      }

      throw error;
    } finally {
      this.isConnecting = false;
    }
  }

  async disconnect(): Promise<void> {
    try {
      await StellarWalletsKit.disconnect();
      this.saveConnectionState(false);
    } catch (error) {
      // Still clear our state even if there's an error
      this.saveConnectionState(false);
    }
  }

  async getPublicKey(skipConnect: boolean = false): Promise<string> {
    ensureKit();

    // The kit keeps the connected address in memory (and its own storage), so
    // this is the non-prompting path.
    try {
      const { address } = await StellarWalletsKit.getAddress();
      if (address) {
        this.connectedAddress = address;
        this.saveConnectionState(true, address, activeWalletId());
        return address;
      }
    } catch (error) {
      // Nothing cached — fall through to asking the wallet.
    }

    if (skipConnect) {
      throw new Error('Failed to get public key from wallet: no active connection');
    }

    // Ask the previously used wallet directly before falling back to the modal.
    const previous = savedWalletType();
    if (previous) {
      try {
        StellarWalletsKit.setWallet(previous);
        const { address } = await StellarWalletsKit.fetchAddress();
        if (address) {
          this.connectedAddress = address;
          this.saveConnectionState(true, address, previous);
          return address;
        }
      } catch (error) {
        // Wallet is gone, locked, or denied access — fall through to a full connect.
      }
    }

    await this.connect();
    const { address } = await StellarWalletsKit.getAddress();
    if (address) {
      this.connectedAddress = address;
      this.saveConnectionState(true, address, activeWalletId());
      return address;
    }
    throw new Error('No address returned from wallet after connection');
  }

  async signTransaction(xdr: string, networkPassphrase?: string): Promise<string> {
    try {
      // For signing, we need a connected wallet, so don't skip connect
      const address = await this.getPublicKey(false);

      const { signedTxXdr } = await StellarWalletsKit.signTransaction(xdr, {
        networkPassphrase: networkPassphrase || Networks.TESTNET,
        address,
      });

      if (!signedTxXdr) {
        throw new Error('No signed transaction returned from wallet');
      }

      return signedTxXdr;
    } catch (error: any) {
      const errorMsg = error?.message || error?.toString() || 'Unknown error';

      // Check if user rejected
      if (errorMsg.includes('rejected') || errorMsg.includes('cancelled') || errorMsg.includes('denied')) {
        throw new Error('Transaction was rejected by user');
      }

      throw new Error(`Failed to sign transaction: ${errorMsg}`);
    }
  }
}
